import React, { useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import { OfferPageActions, OfferPageFilterType } from 'contexts/OfferPage/offerPageStateReducer'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { CALENDAR_GA_EVENT_ACTION, CALENDAR_GA_EVENT_LABEL } from 'constants/analytics'
import OfferCapacityWithAgesSelection from '../Filters/CapacityWithAges/OfferCapacityWithAgesSelection'
import UserAnalyticsActionContext from 'contexts/userAnalyticsActionContext'
import OfferAirportOptions from '../Filters/Airport/OfferAirportOptions'
import ModalBase from 'components/Luxkit/Modal/ModalBase'
import ModalHeader from 'components/Luxkit/Modal/ModalHeader'
import ModalBody from 'components/Luxkit/Modal/ModalBody'
import ModalContent from 'components/Luxkit/Modal/ModalContent'
import ModalFooter from 'components/Luxkit/Modal/ModalFooter'
import useDelayedTogglingBoolean from 'hooks/useDelayedTogglingBoolean'
import { isFlightForceBundled } from 'lib/offer/isFlightForceBundled'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import OfferCalendarSidebarDatePicker from '../Filters/CalendarSidebarPicker/OfferCalendarSidebarDatePicker'
import styled from 'styled-components'
import { rem } from 'polished'
import config from 'constants/config'
import OfferCapacityWithAgesSelectBox from '../Filters/CapacityWithAges/OfferCapacityWithAgesSelectBox'
import OfferCalendarDatePickerSelectBox from '../Filters/CalendarPicker/OfferCalendarDatePickerSelectBox'
import OfferAirportSelectBox from '../Filters/Airport/OfferAirportSelectBox'
import FlashHotelRoomSelectionModal from './FlashHotelMobileVariant/Modals/FlashHotelRoomSelectionModal'
import FlashHotelPackageSelectionModal from './FlashHotelMobileVariant/Modals/FlashHotelPackageSelectionModal'
import { pluralizeToString } from 'lib/string/pluralize'
import { OfferDurationOption } from '../Filters/DurationTabs/OfferDurationTabs'
import { getMultiRoomDurationOptions } from 'selectors/offerPage/multipleRoomBookingSelectors'
import OfferDurationOptions from '../Filters/Duration/OfferDurationOptions'
import { min } from 'lib/array/arrayUtils'
import FlashHotelRoomAndPackageDetailsModal from './FlashHotelMobileVariant/Modals/FlashHotelRoomAndPackageDetailsModal'
import * as Analytics from 'analytics/analytics'
import { getDurationOptions } from 'selectors/offerPage/offerPageSelectors'
import { isMultipleRoomBookingActive } from 'selectors/offerPage/offerPageSharedSelectors'
import { preventRenderWithClosedFilter } from 'lib/offer/offerPageUtil'

const mapFilterToAnalytics: Record<string, string> = {
  capacity: 'guests',
  duration: 'number_of_nights',
  roomSelection: 'select_room',
  packageSelection: 'select_package',
  roomDetails: 'room_details',
}

const FiltersBlock = styled.div`
  flex-grow: 1;

  > * {
    height: ${rem(64)};
    width: 100%;
    padding: 0 ${rem(24)};

    &:not(:first-child):not(:last-child) {
      box-shadow: 0 -1px 0px 0px ${props => props.theme.palette.neutral.default.five};
    }
  }
`

const mobileRoomTestCalendarFilters: Array<OfferPageFilterType> = ['airport', 'duration', 'capacity', 'flights-toggle', 'room']

interface Props {
  className?: string;
  activeFilter?: OfferPageFilterType;
  capacities?: Array<App.RoomCapacity>;
  offer: App.Offer;
  durationOptions: Array<OfferDurationOption>;
}

function FlashHotelSidebarFilters(props: Props) {
  const {
    activeFilter,
    capacities,
    offer,
    durationOptions,
  } = props

  const isMobile = useScreenSizeOnly('mobile')
  const dispatch = useOfferPageDispatch()
  const onUserAnalyticsAction = useContext(UserAnalyticsActionContext)
  const formRef = useRef<HTMLFormElement>(null)

  const closeFilter = useCallback(() => {
    dispatch({ type: OfferPageActions.closeFilter, occupancyPicker: true })
    onUserAnalyticsAction?.(CALENDAR_GA_EVENT_ACTION.CloseFilter, CALENDAR_GA_EVENT_LABEL.CloseCross)
  }, [dispatch, onUserAnalyticsAction])

  const filterContinue = useCallback(() => {
    if (formRef.current?.reportValidity()) {
      dispatch({ type: OfferPageActions.filterContinue })
    }
  }, [dispatch])

  const onBack = useCallback(() => {
    if (formRef.current?.reportValidity()) {
      dispatch({ type: OfferPageActions.filterBack })
    }
  }, [dispatch])

  const onApply = useCallback(() => {
    filterContinue()
  }, [filterContinue])

  // Workaround for bug in React Modal when quickly switching between open/closed
  // https://github.com/reactjs/react-modal/issues/888
  // https://github.com/reactjs/react-modal/issues/808
  // TODO: Come up with better solution, because this will always delay opening the modal
  const delayedOpen = useDelayedTogglingBoolean(!!activeFilter, { onDelay: 300 })
  const minimumDuration = useMemo(() => min(durationOptions, option => option.duration)?.duration ?? 1, [durationOptions])

  useEffect(() => {
    const clientEventSubject = activeFilter ? mapFilterToAnalytics[activeFilter] : undefined
    if (clientEventSubject) {
      Analytics.trackClientEvent({
        subject: clientEventSubject,
        action: 'appear',
        type: 'interaction',
        category: 'offer_funnel',
      })
    }
  }, [activeFilter])

  return (
    <ModalBase
      isOpen={delayedOpen && isMobile}
      onClose={closeFilter}
      mode="drawer"
    >
      <form onSubmit={onApply} ref={formRef}>
        {activeFilter === 'all' && <>
          <ModalHeader
            title={`Your ${config.brandConfig.holidaySynonym}`}
            onCloseButtonClick={closeFilter}
          />
          <ModalBody>
            <FiltersBlock>
              <OfferCapacityWithAgesSelectBox inSidebar />
              <OfferCalendarDatePickerSelectBox inSidebar />
              <OfferAirportSelectBox inSidebar />
            </FiltersBlock>
          </ModalBody>
        </>}
        {activeFilter === 'airport' && <>
          <ModalHeader
            title="Where will you depart from?"
            subtitle="Select your preferred airport"
            onCloseButtonClick={closeFilter}
          />
          <ModalBody>
            <ModalContent>
              <OfferAirportOptions />
            </ModalContent>
          </ModalBody>
          <ModalFooter
            primaryActionProps={{
              children: 'Apply',
              type: 'submit',
            }}
          />
        </>}
        {activeFilter === 'capacity' && <>
          <ModalHeader
            title="Who is going?"
            onCloseButtonClick={closeFilter}
            onBackButtonClick={onBack}
          />
          <ModalBody>
            <OfferCapacityWithAgesSelection
              capacities={capacities}
              disableMultiRoom={isFlightForceBundled(offer)}
            />
          </ModalBody>
          <ModalFooter
            primaryActionProps={{
              children: 'Continue',
              type: 'submit',
            }}
          />
        </>}
        {activeFilter === 'checkin' && <OfferCalendarSidebarDatePicker
          onClose={closeFilter}
          onBack={onBack}
          subFilters={mobileRoomTestCalendarFilters}
        />}

        {activeFilter === 'roomDetails' && <FlashHotelRoomAndPackageDetailsModal
          onClose={closeFilter}
        />}
        {activeFilter === 'roomSelection' && <FlashHotelRoomSelectionModal
          onClose={closeFilter}
          onBack={onBack}
        />}
        {activeFilter === 'packageSelection' && <FlashHotelPackageSelectionModal
          onClose={closeFilter}
          onBack={onBack}
        />}
        {activeFilter === 'duration' && <>
          <ModalHeader
            title="How long do you want to stay?"
            subtitle={minimumDuration > 1 ? `Minimum stay for this offer is ${pluralizeToString('night', minimumDuration)}` : undefined}
            onCloseButtonClick={closeFilter}
            onBackButtonClick={onBack}
          />
          <ModalBody>
            <ModalContent>
              <OfferDurationOptions />
            </ModalContent>
          </ModalBody>
          <ModalFooter
            primaryActionProps={{
              children: 'Continue',
              type: 'submit',
            }}
          />
        </>}
      </form>
    </ModalBase>
  )
}

export default withOfferPageAndReduxState((state, appState) => {
  const isMultiRoom = isMultipleRoomBookingActive(state, appState)
  return {
    activeFilter: state.activeFilter,
    offer: state.offer,
    open: !!state.activeFilter,
    durationOptions: isMultiRoom ? getMultiRoomDurationOptions(state, appState) : getDurationOptions(state, appState),
  }
}, FlashHotelSidebarFilters, preventRenderWithClosedFilter)
